import React, { useCallback, useEffect, useState } from "react";
import { Lock, Pencil, Trash2, Plus, X, Check } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { isoToDisplay } from "./DateInput";

const today = () => new Date().toISOString().slice(0, 10);

const ItemCatatan = ({ c, onChanged }) => {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({ isi: c.isi, tanggal: c.tanggal });
  const simpan = async () => { try { await api.put(`/pastoral/catatan/${c.id}`, f); toast.success("Catatan diperbarui"); setEdit(false); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  const hapus = async () => { if (!window.confirm("Hapus catatan ini?")) return; try { await api.delete(`/pastoral/catatan/${c.id}`); toast.success("Catatan dihapus"); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="rounded-xl border border-slate-200 p-3 bg-white" data-testid={`catatan-item-${c.id}`}>
      {edit ? (
        <div className="space-y-2">
          <Input type="date" value={f.tanggal} onChange={(e) => setF({ ...f, tanggal: e.target.value })} className="min-h-[40px] w-44" data-testid={`catatan-edit-tanggal-${c.id}`} />
          <Textarea value={f.isi} onChange={(e) => setF({ ...f, isi: e.target.value })} rows={3} className="rounded-xl" data-testid={`catatan-edit-isi-${c.id}`} />
          <div className="flex gap-2"><Button size="sm" onClick={simpan} className="rounded-xl" data-testid={`catatan-edit-simpan-${c.id}`}><Check className="h-4 w-4 mr-1" />Simpan</Button><Button size="sm" variant="outline" onClick={() => setEdit(false)} className="rounded-xl"><X className="h-4 w-4 mr-1" />Batal</Button></div>
        </div>
      ) : (
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-xs text-slate-500">{isoToDisplay(c.tanggal)}{c.updated_at ? " · diedit" : ""}</div>
            <p className="text-sm text-slate-800 whitespace-pre-wrap mt-1">{c.isi}</p>
          </div>
          <button onClick={() => setEdit(true)} className="text-slate-400 hover:text-slate-800" aria-label="Edit" data-testid={`catatan-edit-${c.id}`}><Pencil className="h-4 w-4" /></button>
          <button onClick={hapus} className="text-slate-400 hover:text-red-600" aria-label="Hapus" data-testid={`catatan-hapus-${c.id}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      )}
    </div>
  );
};

export const CatatanPribadi = () => {
  const [anggota, setAnggota] = useState([]);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [f, setF] = useState({ jemaat_id: "", isi: "", tanggal: today() });
  const [err, setErr] = useState(null);
  const load = useCallback(() => api.get("/pastoral/catatan").then((r) => setItems(r.data.items)).catch((e) => setErr(errMsg(e))), []);
  useEffect(() => { load(); api.get("/pastoral/anggota-saya").then((r) => setAnggota(r.data.items)).catch(() => {}); }, [load]);
  const tambah = async () => {
    if (!f.jemaat_id) return toast.error("Pilih anggota");
    if (!f.isi.trim()) return toast.error("Isi catatan tidak boleh kosong");
    try { await api.post("/pastoral/catatan", f); toast.success("Catatan ditambahkan"); setF({ jemaat_id: f.jemaat_id, isi: "", tanggal: today() }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (err) return <Card className="p-6 rounded-2xl text-sm text-slate-600" data-testid="catatan-ditolak">{err}</Card>;
  const perAnggota = anggota.map((a) => ({ ...a, catatan: items.filter((c) => c.jemaat_id === a.id) })).filter((a) => a.catatan.length > 0 && (!filter || a.id === filter));
  return (
    <div className="space-y-4" data-testid="catatan-pribadi">
      <div className="flex items-center gap-2 rounded-xl bg-slate-100 text-slate-600 px-3 py-2 text-xs"><Lock className="h-3.5 w-3.5" /> Catatan ini privat — hanya Anda yang dapat melihatnya. Admin dan Pimpinan tidak memiliki akses.</div>
      <Card className="p-4 rounded-2xl space-y-3" data-testid="catatan-form">
        <div className="font-semibold text-slate-900 flex items-center gap-2"><Plus className="h-4 w-4" /> Tambah Catatan</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <select value={f.jemaat_id} onChange={(e) => setF({ ...f, jemaat_id: e.target.value })} className="min-h-[44px] rounded-xl border border-input bg-background px-3 text-sm md:col-span-2" data-testid="catatan-anggota">
            <option value="">Pilih anggota…</option>{anggota.map((a) => <option key={a.id} value={a.id}>{a.nama_lengkap}</option>)}
          </select>
          <Input type="date" value={f.tanggal} onChange={(e) => setF({ ...f, tanggal: e.target.value })} className="min-h-[44px]" data-testid="catatan-tanggal" />
        </div>
        <Textarea value={f.isi} onChange={(e) => setF({ ...f, isi: e.target.value })} placeholder="Tulis catatan khusus tentang anggota ini…" rows={3} className="rounded-xl" data-testid="catatan-isi" />
        <Button onClick={tambah} className="rounded-xl min-h-[44px]" data-testid="catatan-simpan">Simpan Catatan</Button>
      </Card>
      <div className="flex items-center gap-2">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="min-h-[40px] rounded-xl border border-input bg-background px-3 text-sm" data-testid="catatan-filter"><option value="">Semua anggota</option>{anggota.map((a) => <option key={a.id} value={a.id}>{a.nama_lengkap}</option>)}</select>
        <span className="text-xs text-slate-500">{items.length} catatan</span>
      </div>
      {perAnggota.length === 0 && <p className="text-sm text-slate-500 text-center py-6" data-testid="catatan-kosong">Belum ada catatan.</p>}
      {perAnggota.map((a) => (
        <Card key={a.id} className="p-4 rounded-2xl space-y-2" data-testid={`catatan-anggota-${a.id}`}>
          <div className="font-semibold text-slate-900">{a.nama_lengkap} <span className="text-xs font-normal text-slate-500">· {a.catatan.length} catatan</span></div>
          {a.catatan.map((c) => <ItemCatatan key={c.id} c={c} onChanged={load} />)}
        </Card>
      ))}
    </div>
  );
};
